<script setup>
import { useAlert } from 'dashboard/composables';
import AddAutomationRule from './AddAutomationRule.vue';
import EditAutomationRule from './EditAutomationRule.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import { computed, onMounted, ref, watch } from 'vue';
import { until } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import { picoSearch } from '@chatwoot/pico-search';
import AutomationRuleRow from './AutomationRuleRow.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import { BaseTable } from 'dashboard/components-next/table';
import AutomationDateHistory from './components/AutomationDateHistory.vue';
import AutomationAPI from 'dashboard/api/automation';
import { DEFAULT_DELAY_MINUTES } from './constants';
import {
  DATE_REACHED_EVENT,
  apiErrorMessage,
  buildSavedRuleSimulationPayload,
  findDateTriggerTemplate,
} from './dateTrigger';

const getters = useStoreGetters();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();
const confirmDialog = ref(null);
const runNowDialog = ref(null);
const historyRef = ref(null);
const runNowDescription = ref('');

const loading = ref({});
const addDialogRef = ref(null);
const editDialogRef = ref(null);
const showDeleteConfirmationPopup = ref(false);
const selectedAutomation = ref({});
const searchQuery = ref('');
const toggleModalTitle = ref(t('AUTOMATION.TOGGLE.ACTIVATION_TITLE'));
const toggleModalDescription = ref(
  t('AUTOMATION.TOGGLE.ACTIVATION_DESCRIPTION')
);

const records = computed(() => getters['automations/getAutomations'].value);

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, ['name', 'description']);
});

const uiFlags = computed(() => getters['automations/getUIFlags'].value);
const accountId = computed(() => getters.getCurrentAccountId.value);
const accountUiFlags = useMapGetter('accounts/getUIFlags');

const isDelayedAutomationsEnabled = computed(() =>
  getters['accounts/isFeatureEnabledonAccount'].value(
    accountId.value,
    'delayed_automations'
  )
);

const isDateRule = automation => automation.event_name === DATE_REACHED_EVENT;

const instantRecords = computed(() =>
  filteredRecords.value.filter(
    automation => !automation.execution_delay && !isDateRule(automation)
  )
);
const delayedRecords = computed(() =>
  filteredRecords.value.filter(automation => automation.execution_delay)
);
const dateRecords = computed(() => filteredRecords.value.filter(isDateRule));

const RECORDS_BY_TAB = {
  instant: instantRecords,
  delayed: delayedRecords,
  date: dateRecords,
};

// Accounts that can't create delayed rules, and have none left over, just see the plain list.
const showTabs = computed(
  () =>
    isDelayedAutomationsEnabled.value ||
    records.value.some(
      automation => automation.execution_delay || isDateRule(automation)
    )
);

// ?new=date (aba Campanhas do CRM) abre a lista já na aba Em uma data.
const NEW_DATE_RULE = 'date';
const activeTab = ref(route.query.new === NEW_DATE_RULE ? 'date' : 'instant');

const tabs = computed(() => [
  {
    key: 'instant',
    label: t('AUTOMATION.LIST.TABS.INSTANT'),
    count: instantRecords.value.length,
  },
  {
    key: 'delayed',
    label: t('AUTOMATION.LIST.TABS.DELAYED'),
    count: delayedRecords.value.length,
  },
  {
    key: 'date',
    label: t('AUTOMATION.DATE_TRIGGER.LIST.TAB'),
    count: dateRecords.value.length,
  },
]);

const activeTabIndex = computed(() =>
  tabs.value.findIndex(tab => tab.key === activeTab.value)
);

const visibleRecords = computed(() => {
  if (!showTabs.value) return filteredRecords.value;
  return RECORDS_BY_TAB[activeTab.value].value;
});

const noDataMessage = computed(() => {
  if (searchQuery.value) return t('AUTOMATION.NO_RESULTS');
  if (showTabs.value && activeTab.value === 'delayed') {
    return t('AUTOMATION.LIST.404_DELAYED');
  }
  if (showTabs.value && activeTab.value === 'date') {
    return t('AUTOMATION.DATE_TRIGGER.LIST.404');
  }
  return t('AUTOMATION.LIST.404');
});

const onTabChanged = tab => {
  activeTab.value = tab.key;
};

const deleteConfirmText = computed(
  () => `${t('AUTOMATION.DELETE.CONFIRM.YES')} ${selectedAutomation.value.name}`
);

const deleteRejectText = computed(
  () => `${t('AUTOMATION.DELETE.CONFIRM.NO')} ${selectedAutomation.value.name}`
);

const deleteMessage = computed(() => ` ${selectedAutomation.value.name}?`);

const isSLAEnabled = computed(() =>
  getters['accounts/isFeatureEnabledonAccount'].value(accountId.value, 'sla')
);

let slaFetchPromise;

// Account feature flags may load after this page mounts, so watch the SLA flag
// to ensure its options are fetched after a hard refresh.
watch(
  isSLAEnabled,
  isEnabled => {
    if (isEnabled) {
      slaFetchPromise = store.dispatch('sla/get');
    }
  },
  { immediate: true }
);

const showDelayDisabledBanner = computed(
  () =>
    !isDelayedAutomationsEnabled.value &&
    records.value.some(automation => automation.execution_delay)
);

const openAddPopup = () => {
  const startsWithWait =
    isDelayedAutomationsEnabled.value && activeTab.value === 'delayed';
  addDialogRef.value?.open(startsWithWait ? DEFAULT_DELAY_MINUTES : null, {
    startsOnDate: showTabs.value && activeTab.value === 'date',
  });
};

const openHistory = automation => {
  historyRef.value?.open(automation);
};

// The confirmation quotes today's simulation of the saved rule, so the admin sees how many
// records the manual run will hit (the ones already executed are not repeated).
const runDateTriggerNow = async automation => {
  loading.value[automation.id] = true;
  try {
    const { data } = await AutomationAPI.simulateDateTrigger(
      buildSavedRuleSimulationPayload(automation)
    );
    runNowDescription.value = data.blocked_by_limit
      ? t('AUTOMATION.DATE_TRIGGER.SIMULATION.BLOCKED', {
          count: data.matched_count,
          limit: data.limit,
        })
      : t(
          'AUTOMATION.DATE_TRIGGER.RUN_NOW.CONFIRM',
          { count: data.would_execute_count },
          data.would_execute_count
        );
  } catch (error) {
    useAlert(
      apiErrorMessage(error) || t('AUTOMATION.DATE_TRIGGER.SIMULATION.ERROR')
    );
    return;
  } finally {
    loading.value[automation.id] = false;
  }

  const confirmed = await runNowDialog.value.showConfirmation();
  if (!confirmed) return;

  try {
    await AutomationAPI.runDateTriggerNow(automation.id);
    useAlert(t('AUTOMATION.DATE_TRIGGER.RUN_NOW.SUCCESS'));
  } catch (error) {
    useAlert(
      apiErrorMessage(error) || t('AUTOMATION.DATE_TRIGGER.RUN_NOW.ERROR')
    );
  }
};
const hideAddPopup = () => {
  addDialogRef.value?.close();
};

const openEditPopup = async response => {
  selectedAutomation.value = { ...response };
  await until(() => accountUiFlags.value.isFetchingItem).toBe(false);
  if (isSLAEnabled.value) {
    slaFetchPromise ||= store.dispatch('sla/get');
    await slaFetchPromise;
  }
  editDialogRef.value?.open(response);
};

// Links da aba Campanhas do CRM: ?new=date&template=<modelo> cria uma automação Em uma data
// (com o modelo, se houver); ?edit=<id> e ?history=<id> abrem a automação. A query é limpa
// para não reabrir ao recarregar.
const openFromQuery = async automationsLoaded => {
  const { new: newRule, template, edit, history } = route.query;
  if (newRule === NEW_DATE_RULE) {
    addDialogRef.value?.open(null, {
      startsOnDate: true,
      templateKey: findDateTriggerTemplate(template) ? template : null,
    });
  }
  if (edit || history) {
    await automationsLoaded;
    const automation = records.value.find(
      record => record.id === Number(edit || history)
    );
    if (automation && edit) openEditPopup(automation);
    else if (automation) openHistory(automation);
  }
  if (newRule || edit || history) router.replace({ query: {} });
};

onMounted(async () => {
  store.dispatch('inboxes/get');
  store.dispatch('agents/get');
  store.dispatch('contacts/get');
  store.dispatch('teams/get');
  store.dispatch('labels/get');
  store.dispatch('campaigns/get');
  const automationsLoaded = store.dispatch('automations/get');
  await openFromQuery(automationsLoaded);
});
const hideEditPopup = () => {
  editDialogRef.value?.close();
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedAutomation.value = response;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteAutomation = async id => {
  try {
    await store.dispatch('automations/delete', id);
    useAlert(t('AUTOMATION.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AUTOMATION.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[selectedAutomation.value.id] = false;
  }
};
const confirmDeletion = () => {
  loading.value[selectedAutomation.value.id] = true;
  closeDeletePopup();
  deleteAutomation(selectedAutomation.value.id);
};
const cloneAutomation = async ({ id }) => {
  try {
    await store.dispatch('automations/clone', id);
    useAlert(t('AUTOMATION.CLONE.API.SUCCESS_MESSAGE'));
    store.dispatch('automations/get');
  } catch (error) {
    useAlert(t('AUTOMATION.CLONE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[selectedAutomation.value.id] = false;
  }
};

const submitAutomation = async (payload, mode) => {
  try {
    const action =
      mode === 'edit' ? 'automations/update' : 'automations/create';
    const successMessage =
      mode === 'edit'
        ? t('AUTOMATION.EDIT.API.SUCCESS_MESSAGE')
        : t('AUTOMATION.ADD.API.SUCCESS_MESSAGE');
    await store.dispatch(action, payload);
    useAlert(successMessage);
    hideAddPopup();
    hideEditPopup();
  } catch (error) {
    const fallbackMessage =
      mode === 'edit'
        ? t('AUTOMATION.EDIT.API.ERROR_MESSAGE')
        : t('AUTOMATION.ADD.API.ERROR_MESSAGE');
    useAlert(apiErrorMessage(error) || fallbackMessage);
  }
};
const toggleAutomation = async ({ id, name, status }) => {
  try {
    if (status) {
      toggleModalTitle.value = t('AUTOMATION.TOGGLE.DEACTIVATION_TITLE');
      toggleModalDescription.value = t(
        'AUTOMATION.TOGGLE.DEACTIVATION_DESCRIPTION',
        {
          automationName: name,
        }
      );
    } else {
      toggleModalTitle.value = t('AUTOMATION.TOGGLE.ACTIVATION_TITLE');
      toggleModalDescription.value = t(
        'AUTOMATION.TOGGLE.ACTIVATION_DESCRIPTION',
        {
          automationName: name,
        }
      );
    }

    const ok = await confirmDialog.value.showConfirmation();
    if (ok) {
      await store.dispatch('automations/update', {
        id: id,
        active: !status,
      });
      const message = status
        ? t('AUTOMATION.TOGGLE.DEACTIVATION_SUCCESFUL')
        : t('AUTOMATION.TOGGLE.ACTIVATION_SUCCESFUL');
      useAlert(message);
    }
  } catch (error) {
    useAlert(apiErrorMessage(error) || t('AUTOMATION.EDIT.API.ERROR_MESSAGE'));
  }
};

const tableHeaders = computed(() => {
  return [
    t('AUTOMATION.LIST.TABLE_HEADER.NAME'),
    t('AUTOMATION.LIST.TABLE_HEADER.ACTIVE'),
    t('AUTOMATION.LIST.TABLE_HEADER.CREATED_ON'),
    t('AUTOMATION.LIST.TABLE_HEADER.ACTIONS'),
  ];
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('AUTOMATION.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('AUTOMATION.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('AUTOMATION.HEADER')"
        :description="$t('AUTOMATION.DESCRIPTION')"
        :link-text="$t('AUTOMATION.LEARN_MORE')"
        :search-placeholder="$t('AUTOMATION.SEARCH_PLACEHOLDER')"
        feature-name="automation"
      >
        <template v-if="showTabs" #tabs>
          <TabBar
            :tabs="tabs"
            :initial-active-tab="activeTabIndex"
            @tab-changed="onTabChanged"
          />
        </template>
        <template v-if="visibleRecords.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('AUTOMATION.COUNT', { n: visibleRecords.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="$t('AUTOMATION.HEADER_BTN_TXT')"
            size="sm"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div
        v-if="showDelayDisabledBanner"
        class="px-4 py-3 mb-4 text-sm rounded-lg bg-n-amber-3 text-n-amber-12"
      >
        {{ $t('AUTOMATION.LIST.DELAY_DISABLED_BANNER') }}
      </div>
      <BaseTable
        class="rounded-2xl border border-n-weak bg-white dark:bg-n-solid-2 px-5 overflow-hidden [&_thead]:border-t-0"
        :headers="tableHeaders"
        :items="visibleRecords"
        :no-data-message="noDataMessage"
      >
        <template #row="{ items }">
          <AutomationRuleRow
            v-for="automation in items"
            :key="automation.id"
            :automation="automation"
            :loading="loading[automation.id]"
            @clone="cloneAutomation"
            @toggle="toggleAutomation"
            @edit="openEditPopup"
            @delete="openDeletePopup"
            @history="openHistory"
            @run-now="runDateTriggerNow"
          />
        </template>
      </BaseTable>
    </template>

    <AddAutomationRule ref="addDialogRef" @save-automation="submitAutomation" />

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AUTOMATION.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />

    <EditAutomationRule
      ref="editDialogRef"
      :selected-response="selectedAutomation"
      @save-automation="submitAutomation"
    />
    <woot-confirm-modal
      ref="confirmDialog"
      :title="toggleModalTitle"
      :description="toggleModalDescription"
    />
    <woot-confirm-modal
      ref="runNowDialog"
      :title="$t('AUTOMATION.DATE_TRIGGER.RUN_NOW.TITLE')"
      :description="runNowDescription"
      :confirm-label="$t('AUTOMATION.DATE_TRIGGER.RUN_NOW.CONFIRM_BUTTON')"
      :cancel-label="$t('AUTOMATION.DATE_TRIGGER.RUN_NOW.CANCEL_BUTTON')"
    />
    <AutomationDateHistory ref="historyRef" />
  </SettingsLayout>
</template>
